import React from 'react';
import { Check, ArrowRight, Sparkles } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { useLanguage } from '@/contexts/LanguageContext';

const PlaygroundValuesSection = () => {
  const { language } = useLanguage();
  
  const benefits = language === 'en' ? [
    {
      title: "Benefits for you",
      subtitle: "Benefits for Business Owners",
      image: "https://images.unsplash.com/photo-1581091226825-a6a2a5aee158",
      items: [
        {
          title: "Liquidity Options", 
          description: "Flexible solutions tailored to your financial goals, allowing you to diversify your personal wealth."
        },
        {
          title: "Customized Deal Structure", 
          description: "After closing, you can choose to retire, lead, or anything in between."
        },
        {
          title: "Fair Valuation", 
          description: "We recognize the true value of what you've built, both tangible assets and intangible elements."
        }
      ]
    }, {
      title: "Benefits for your business",
      subtitle: "Benefits for Company Growth",
      image: "https://images.unsplash.com/photo-1460925895917-afdab827c52f",
      items: [
        {
          title: "Permanent Home", 
          description: "A forever home for your company, ensuring its legacy continues without risk of being flipped."
        },
        {
          title: "Resources for Growth", 
          description: "Access to capital, technology, and expertise to help your business reach its full potential."
        },
        {
          title: "Market Expansion", 
          description: "Leverage our network to enter new markets and reach previously inaccessible customers."
        }
      ]
    }, {
      title: "Benefits for your employees",
      subtitle: "Benefits for Employee Success",
      image: "/lovable-uploads/3979cfb3-f61f-48a9-ad9f-78658796b3b7.png",
      items: [
        {
          title: "Culture Preservation", 
          description: "We maintain the unique culture that makes your team special, preserving their work environment."
        },
        {
          title: "Professional Development", 
          description: "Enhanced career growth through training, cross-portfolio collaboration, and expert mentorship."
        },
        {
          title: "Stability & Security", 
          description: "Long-term employment security with improved benefits and clear advancement paths."
        }
      ]
    }
  ] : [{
    title: "Beneficios para ti",
    subtitle: "Beneficios para Propietarios de Negocios",
    image: "https://images.unsplash.com/photo-1581091226825-a6a2a5aee158",
    items: [
      {
        title: "Opciones de Liquidez", 
        description: "Soluciones flexibles adaptadas a tus objetivos financieros, permitiéndote diversificar tu patrimonio personal."
      },
      {
        title: "Estructura de Acuerdo Personalizada", 
        description: "Después del cierre, puedes elegir retirarte, liderar, o cualquier opción intermedia."
      },
      {
        title: "Valuación Justa", 
        description: "Reconocemos el valor real de lo que has construido, incluyendo activos tangibles e intangibles."
      }
    ]
  }, {
    title: "Beneficios para tu negocio",
    subtitle: "Beneficios para el Crecimiento de la Empresa",
    image: "https://images.unsplash.com/photo-1460925895917-afdab827c52f",
    items: [
      {
        title: "Hogar Permanente", 
        description: "Un hogar permanente para tu empresa, asegurando que su legado continúe sin riesgos de reventa."
      },
      {
        title: "Recursos para Crecer", 
        description: "Acceso a capital, tecnología y experiencia para ayudar a tu negocio a alcanzar su máximo potencial."
      },
      {
        title: "Expansión de Mercado", 
        description: "Aprovecha nuestra red para entrar en nuevos mercados y llegar a clientes anteriormente inaccesibles."
      }
    ]
  }, {
    title: "Beneficios para tus empleados",
    subtitle: "Beneficios para el Éxito de los Empleados",
    image: "/lovable-uploads/3979cfb3-f61f-48a9-ad9f-78658796b3b7.png",
    items: [
      {
        title: "Preservación de la Cultura", 
        description: "Mantenemos la cultura única que hace especial a tu equipo, preservando su entorno laboral."
      },
      {
        title: "Desarrollo Profesional", 
        description: "Mejores oportunidades de carrera mediante capacitación, colaboración entre empresas y mentoría especializada."
      },
      {
        title: "Estabilidad y Seguridad", 
        description: "Seguridad laboral a largo plazo con beneficios mejorados y caminos claros para el avance profesional."
      }
    ]
  }];

  return (
    <section className="py-20 bg-white dark:bg-noa-rich-black/95 relative overflow-hidden">
      <div className="absolute inset-0 bg-dot-pattern opacity-20"></div>
      <div className="absolute inset-0 bg-gradient-to-br from-white/5 to-white/20"></div>
      
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl relative z-10">
        <div className="flex flex-col items-start text-left mb-12">
          <p className="inline-flex items-center justify-center gap-2 bg-noa-rich-black px-4 py-2 rounded-full text-white text-sm font-medium mb-4">
            <img src="/lovable-uploads/491ec6e6-36f3-4c3d-a609-b1da4cbe54d4.png" alt="Noa Logo" className="h-4 w-4" />
            <span>Noa Partners</span>
          </p>
          
          <h2 className="font-space-grotesk text-3xl md:text-4xl font-bold text-noa-rich-black dark:text-noa-antiflash-white leading-tight mb-4">
            {language === 'en' ? 
              "Benefits of Partnering with Noa" : 
              "Beneficios de Asociarse con Noa"}<span className="text-blue-600">.</span>
          </h2>
          <p className="text-base text-gray-600 dark:text-gray-300 max-w-3xl">
            {language === 'en' ?
              "Tailored acquisition partnerships benefiting entrepreneurs, businesses, and teams for long-term value." :
              "Asociaciones de adquisición personalizadas que benefician a emprendedores, empresas y equipos para asegurar crecimiento sostenible."}
          </p>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {benefits.map((category, index) => (
            <div key={index} className="bg-white/50 dark:bg-noa-rich-black/50 rounded-xl p-6 border border-gray-100 dark:border-gray-800 shadow-sm hover:shadow-md transition-shadow duration-300">
              <div className="h-48 overflow-hidden rounded-lg mb-4">
                <img 
                  src={category.image} 
                  alt={category.title} 
                  className="w-full h-full object-cover object-center transition-transform duration-500 hover:scale-105"
                />
              </div>
              
              <h3 className="text-lg font-bold text-noa-rich-black dark:text-noa-antiflash-white mb-3 font-space-grotesk">
                {category.title}
              </h3>
              <div className="w-10 h-1 bg-blue-600 mb-4"></div>
              
              <ul className="space-y-3">
                {category.items.map((item, i) => (
                  <li key={i} className="flex items-start gap-2">
                    <div className="mt-1 bg-blue-500 rounded-full p-1 flex-shrink-0">
                      <Check className="text-white" size={10} strokeWidth={3} />
                    </div>
                    <div>
                      <h4 className="text-noa-rich-black dark:text-noa-antiflash-white font-medium text-sm mb-0.5">
                        {item.title}
                      </h4>
                      <p className="text-gray-600 dark:text-gray-400 text-xs">
                        {item.description}
                      </p>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default PlaygroundValuesSection;
